/**
 * Branding model shared by the platform API (validation, OpenAPI) and the apps (forms, runtime).
 *
 * Effective settings = built-in defaults ← Ansible defaults (`platform_branding`) ← admin changes
 * (API). Fields listed in `locked` come from Ansible only and cannot be changed through the API.
 */
import { z } from 'zod';
import { SCALE_STEPS } from './theme.js';

export const THEMES = ['system', 'light', 'dark'] as const;
export const DENSITIES = ['compact', 'comfortable'] as const;
export const CONTRAST_POLICIES = ['adjust', 'strict'] as const;

/** Image slots. Logos: SVG/PNG/WebP; the login background also accepts JPEG. */
export const ASSET_SLOTS = ['logoLight', 'logoDark', 'favicon', 'loginBackground'] as const;
export type AssetSlot = (typeof ASSET_SLOTS)[number];

const localeTag = z.string().regex(/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,4}){0,2}$/, 'must be a language tag like "de" or "pt-BR"');
export const instanceNameSchema = z.string().trim().min(1).max(60);
export const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'must be a colour like #1d4ed8'); // stored lower case (the API normalizes)

export const brandingSettingsSchema = z
  .object({
    instanceName: instanceNameSchema.describe('Name of this instance, e.g. "Example Mail": header, login, document title, mails'),
    instanceNameByLocale: z
      .record(localeTag, instanceNameSchema)
      .describe('Optional per-language instance names, e.g. {"de": "Beispiel Post"}'),
    primaryColor: hexColorSchema.nullable().describe('Brand colour #rrggbb; null = built-in blue'),
    contrastPolicy: z
      .enum(CONTRAST_POLICIES)
      .describe('adjust = change the lightness until WCAG AA passes; strict = reject colours that fail in the light theme'),
    defaultTheme: z.enum(THEMES).describe('Theme for users without their own choice'),
    defaultDensity: z.enum(DENSITIES).describe('Density for users without their own choice'),
  })
  .describe('BrandingSettings');

export type BrandingSettings = z.infer<typeof brandingSettingsSchema>;
export type BrandingField = keyof BrandingSettings;
export const BRANDING_FIELDS = Object.keys(brandingSettingsSchema.shape) as BrandingField[];

/** Everything Ansible can lock: settings fields and image slots. */
export const LOCKABLE_FIELDS = [
  'instanceName',
  'instanceNameByLocale',
  'primaryColor',
  'contrastPolicy',
  'defaultTheme',
  'defaultDensity',
  ...ASSET_SLOTS,
] as const satisfies readonly (BrandingField | AssetSlot)[];
export type LockableField = (typeof LOCKABLE_FIELDS)[number];

/** Defaults before Ansible or an admin changed anything. */
export const BUILTIN_BRANDING: BrandingSettings = {
  instanceName: 'Grommunio',
  instanceNameByLocale: {},
  primaryColor: null,
  contrastPolicy: 'adjust',
  defaultTheme: 'system',
  defaultDensity: 'compact',
};

const hex = z.string().regex(/^#[0-9a-f]{6}$/);

const brandColorsSchema = z.object({ brand: hex, brandForeground: hex, accent: hex });

export const brandThemeSchema = z
  .object({
    light: brandColorsSchema,
    dark: brandColorsSchema,
    scale: z.object(Object.fromEntries(SCALE_STEPS.map((step) => [String(step), hex])) as Record<`${(typeof SCALE_STEPS)[number]}`, typeof hex>),
  })
  .describe('BrandTheme');

const themeCheckSchema = z.object({
  brandOnSurface: z.number(),
  foregroundOnBrand: z.number(),
  textOnAccent: z.number(),
  adjusted: z.boolean(),
});

export const brandThemeReportSchema = z
  .object({ input: hex, light: themeCheckSchema, dark: themeCheckSchema, minContrast: z.number() })
  .describe('BrandThemeReport');

export const brandingAssetSchema = z
  .object({
    url: z.string().describe('Path below the API root, e.g. /api/v1/branding/assets/3f2a….svg (immutable, cacheable)'),
    contentType: z.enum(['image/svg+xml', 'image/png', 'image/webp', 'image/jpeg']),
    bytes: z.number().int(),
    width: z.number().int().nullable(),
    height: z.number().int().nullable(),
    source: z.enum(['ansible', 'api']),
  })
  .describe('BrandingAsset');

export type BrandingAsset = z.infer<typeof brandingAssetSchema>;

export const publicBrandingSchema = z
  .object({
    revision: z.number().int().describe('Increases with every change (also the ETag)'),
    settings: brandingSettingsSchema,
    theme: brandThemeSchema.nullable().describe('Generated brand tokens; null = built-in theme'),
    report: brandThemeReportSchema.nullable().describe('Contrast report of the brand colour'),
    assets: z.object(
      Object.fromEntries(ASSET_SLOTS.map((slot) => [slot, brandingAssetSchema.nullable()])) as Record<AssetSlot, z.ZodNullable<typeof brandingAssetSchema>>,
    ),
    locked: z.array(z.enum(LOCKABLE_FIELDS)).describe('Fields owned by Ansible (read-only)'),
    overridden: z.array(z.string()).describe('Fields changed through the API (differ from the defaults)'),
  })
  .describe('Branding');

export type PublicBranding = z.infer<typeof publicBrandingSchema>;

/** The instance name for a locale (per-language override, base language, then the default). */
export function instanceNameFor(settings: Pick<BrandingSettings, 'instanceName' | 'instanceNameByLocale'>, locale: string): string {
  const byLocale = settings.instanceNameByLocale;
  return byLocale[locale] ?? byLocale[locale.split('-')[0] ?? locale] ?? settings.instanceName;
}
